import { useQueries, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAvailableTargets } from '../../api/targets-client/targets-client.hooks';
import type { GetAvailableTargetsResponse } from '../../api/targets-client/targets-client.type';
import {
  getWorkPeriodExternalGoal,
  getWorkPeriodGoals,
  getWorkPeriodsMeasure,
} from '../../api/work-periods-client/work-periods-client';
import { useAvailableMeasures } from '../../api/work-periods-client/work-periods-client.hooks';
import type {
  MeasureMetadata,
  WorkPeriod,
  WorkPeriodGoal,
} from '../../api/work-periods-client/work-periods-client.type';
import { Measure, MeasureUnits, ModuleType, Transformer } from '../../api/work-periods-client/work-periods-client.type';
import { useOrganizationId } from '../../helpers/auth-helpers/auth.hooks';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useDateRange, useTimeAllocation } from '../../store/process-analysis-store/process-analysis-store.hooks';
import { interpolateMeasureMetadata } from './process-analysis-shared.helpers';

const timeAllocationTransformerMeasures = [Measure.DeclinedChangeRequests, Measure.ReviewTime, Measure.Readiness];

function useWorkPeriodGoals({ workPeriodId }: { workPeriodId: string | undefined }): {
  data: WorkPeriodGoal[] | undefined;
  query: UseQueryResult<WorkPeriodGoal[]>;
} {
  const query = useQuery({
    queryKey: ['workPeriodGoals', workPeriodId],
    queryFn: ({ signal }) =>
      workPeriodId ? getWorkPeriodGoals(workPeriodId, signal) : Promise.reject('Work period ID is required'),
    enabled: !!workPeriodId,
  });
  return { data: query.data, query };
}

function useWorkPeriodExternalGoal({ workPeriodId }: { workPeriodId: string | undefined }): {
  data: string | undefined;
  query: UseQueryResult<{ external_goal: string }>;
} {
  const query = useQuery({
    queryKey: ['workPeriodExternalGoal', workPeriodId],
    queryFn: ({ signal }) =>
      workPeriodId ? getWorkPeriodExternalGoal(workPeriodId, signal) : Promise.reject('Work period ID is required'),
    enabled: !!workPeriodId,
  });
  return { data: query.data?.external_goal, query };
}

const useSprintComparisonViewData = (selectedWorkPeriods: WorkPeriod[]) => {
  const { t } = useTranslation();
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const hasWorkPeriods = selectedWorkPeriods.length > 0;

  const { data: availableMeasuresResponse, query: availableMeasuresQuery } = useAvailableMeasures({
    payload: {
      scope: [ModuleType.WorkPeriod],
      work_period_id: hasWorkPeriods ? selectedWorkPeriods[0].id : '',
    },
    options: {
      enabled: hasWorkPeriods,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  });

  const availableMeasures = availableMeasuresResponse?.work_period?.measures || [];
  const customMeasuresRaw = availableMeasures.filter(
    (measure: MeasureMetadata) => measure.is_custom && measure.measure_units !== MeasureUnits.Text,
  );

  const customMeasures = interpolateMeasureMetadata(customMeasuresRaw, t);
  const measureNames = customMeasures.map((measure) => measure.measure_name);

  const workPeriodBatchSize = 3;
  const measureBatchSize = 3;

  const batchedWorkPeriods = selectedWorkPeriods.reduce((acc, item, index) => {
    const batchIndex = Math.floor(index / workPeriodBatchSize);
    if (!acc[batchIndex]) {
      acc[batchIndex] = [];
    }
    acc[batchIndex].push(item);
    return acc;
  }, [] as WorkPeriod[][]);

  const batchedMeasures = measureNames.reduce((acc, item, index) => {
    const batchIndex = Math.floor(index / measureBatchSize);
    if (!acc[batchIndex]) {
      acc[batchIndex] = [];
    }
    acc[batchIndex].push(item);
    return acc;
  }, [] as string[][]);

  const queries = useQueries({
    queries: batchedWorkPeriods.flatMap((workPeriodBatch) =>
      workPeriodBatch.flatMap((workPeriod) =>
        batchedMeasures.map((measureBatch) => ({
          queryKey: [
            'work-periods-measure',
            workPeriod.id,
            workPeriod.subproject,
            measureBatch,
            selectedWorkPeriods.map((wp) => `${wp.id}:${wp.subproject}`).join('|'),
            organizationId,
          ],
          queryFn: ({ signal }: { signal: AbortSignal }) =>
            getWorkPeriodsMeasure({
              subproject_id: workPeriod.subproject,
              work_period_id: workPeriod.id,
              start_date: dayjs(workPeriod.start_date).format('YYYY-MM-DD'),
              end_date: dayjs(workPeriod.end_date).format('YYYY-MM-DD'),
              measure_name: measureBatch,
              is_custom: true,
              module_type: ModuleType.WorkPeriod,
              transformer: Transformer.ReturnFirstMetricOnly,
              superuser_force_recalculation: superuserMode.enabled ? superuserMode.forceRecalculation : false,
              organization_id: organizationId || undefined,
              signal,
            }),
          enabled: hasWorkPeriods && measureNames.length > 0 && !!organizationId,
          retry: 3,
          retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),
          gcTime: 5 * 60 * 1000,
          staleTime: 30000,
        })),
      ),
    ),
  });

  const isFetching = availableMeasuresQuery.isFetching || queries.some((query) => query.isFetching);
  // `isLoading` is true while we are still resolving the comparison view's
  // first-paint data and no cached value is available. It folds in:
  //   1. The gating `useAvailableMeasures` query — until its response lands
  //      we have no `measureNames`, so the per-measure queries are idle and
  //      cannot themselves report `isLoading`. Without this, the view would
  //      flash an empty comparison between mount and metadata arrival.
  //   2. The per-measure queries' own `isLoading` flags once enabled.
  // Background refetches do NOT trip `isLoading` (that's `isFetching`'s job),
  // which keeps cached navigations free of overlay flashes.
  const isLoading =
    hasWorkPeriods &&
    (availableMeasuresQuery.isLoading || (queries.length > 0 && queries.some((query) => query.isLoading)));
  const measureData = queries.reduce(
    (acc, query, index) => {
      if (query.data) {
        const workPeriodIndex = batchedMeasures.length === 0 ? 0 : Math.floor(index / batchedMeasures.length);
        const workPeriod = selectedWorkPeriods[workPeriodIndex];

        if (!acc[workPeriod.id]) {
          acc[workPeriod.id] = {};
        }

        acc[workPeriod.id] = { ...acc[workPeriod.id], ...query.data };
      }
      return acc;
    },
    {} as Record<string, Record<string, unknown>>,
  );

  return {
    customMeasures,
    measureData,
    isFetching,
    isLoading,
    hasWorkPeriods,
  };
};

const useSprintAvailableTargets = (workPeriods: WorkPeriod[]): GetAvailableTargetsResponse | undefined => {
  const organizationId = useOrganizationId();
  const { startDate, endDate } = useDateRange();
  const timeAllocation = useTimeAllocation();

  const work_period_ids = workPeriods.map((workPeriod) => workPeriod.id).join(',');
  const { data: availableTargetsResponse } = useAvailableTargets({
    payload: {
      organization_id: organizationId || '',
      work_period_ids,
      start_date: dayjs(startDate).format('YYYY-MM-DD'),
      end_date: dayjs(endDate).format('YYYY-MM-DD'),
      time_allocation_type: timeAllocation,
    },
  });

  return availableTargetsResponse;
};

export {
  timeAllocationTransformerMeasures,
  useSprintAvailableTargets,
  useSprintComparisonViewData,
  useWorkPeriodExternalGoal,
  useWorkPeriodGoals,
};
